import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import * as db from "@/lib/db.server";

/** Intent filters surface "Available today / Telehealth / Accepting new patients" pages.
 * Pages stay noindex until a real provider matches the intent for that specialty+city. */
export type IntentKind = "available_today" | "accepting_new_patients" | "telehealth";

const intentSchema = z
  .object({
    specialty: z.string().min(1).max(100),
    city: z.string().min(1).max(100),
    state: z.string().length(2).optional(),
    kind: z.enum(["available_today", "accepting_new_patients", "telehealth"]),
  })
  .strict();

/** Returns providers in (specialty, city) matching the requested intent.
 * Used by intent route shells to decide indexability + render listings.
 * Public SEO endpoint (no auth) — reads only active, non-deleted providers. */
export const getIntentProviders = createServerFn({ method: "GET" })
  .inputValidator((input) => intentSchema.parse(input))
  .handler(async ({ data }) => {
    const conditions: string[] = [
      "is_active = true",
      "is_deleted = false",
      "specialty ILIKE $1",
      "city ILIKE $2",
    ];
    const params: unknown[] = [data.specialty, data.city];

    if (data.state) {
      params.push(data.state);
      conditions.push(`state = $${params.length}`);
    }

    if (data.kind === "available_today") {
      // proxy: providers that expose availability days or instant booking
      conditions.push("(accepts_instant_booking = true OR in_app_calendar_active = true)");
    } else if (data.kind === "telehealth") {
      // best-effort: no dedicated column yet; require in_app_calendar_active as proxy
      conditions.push("in_app_calendar_active = true");
    } else if (data.kind === "accepting_new_patients") {
      conditions.push("accepts_instant_booking = true");
    }

    const where = conditions.join(" AND ");

    const rows = await db.query<{
      id: string;
      public_id: string | null;
      name: string | null;
      credentials: string | null;
      specialty: string | null;
      practice: string | null;
      city: string | null;
      state: string | null;
      available_days: any;
      accepts_instant_booking: boolean | null;
      in_app_calendar_active: boolean | null;
    }>(
      `SELECT id, public_id, name, credentials, specialty, practice, city, state, available_days, accepts_instant_booking, in_app_calendar_active
         FROM public.providers
        WHERE ${where}
        LIMIT 50`,
      params,
    );

    const countRow = await db.one<{ count: number }>(
      `SELECT count(*)::int AS count FROM public.providers WHERE ${where}`,
      params,
    );

    return { providers: rows, total: countRow?.count ?? 0 };
  });

/** Materializes the highest-value specialty+city landing pages into seo_pages.
 * Priority = provider_count desc. Admin-triggered; preserves existing lazy upsert. */
export const materializeHighValueLandingPages = createServerFn({ method: "POST" })
  .inputValidator((input) => z.object({ limit: z.number().int().min(1).max(2000).default(500) }).parse(input))
  .handler(async ({ data }) => {
    // Aggregate provider counts per (specialty, city, state), top N combos.
    const rows = await db.query<{
      specialty: string;
      city: string;
      state: string;
    }>(
      `SELECT specialty, city, state
         FROM public.providers
        WHERE is_active = true
          AND is_deleted = false
          AND specialty IS NOT NULL
          AND city IS NOT NULL
          AND state IS NOT NULL
        LIMIT 20000`,
    );

    const counts = new Map<string, { specialty: string; city: string; state: string; n: number }>();
    for (const r of rows ?? []) {
      const key = `${r.specialty}|${r.city}|${r.state}`;
      const cur = counts.get(key);
      if (cur) cur.n += 1;
      else counts.set(key, { specialty: r.specialty as string, city: r.city as string, state: r.state as string, n: 1 });
    }

    const top = [...counts.values()].sort((a, b) => b.n - a.n).slice(0, data.limit);

    const slugify = (s: string) =>
      s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

    const upserts = top.map((t) => {
      const slug = `/specialty/${slugify(t.specialty)}/${slugify(t.city)}-${t.state.toLowerCase()}`;
      return {
        page_type: "specialty_city" as const,
        slug,
        specialty: t.specialty,
        city: t.city,
        state: t.state,
        insurance_id: null as string | null,
        provider_count: t.n,
        title: `${t.specialty} in ${t.city}, ${t.state} | Heallexa`,
        description: `Find ${t.specialty.toLowerCase()} in ${t.city}, ${t.state}. Compare ${t.n}+ providers, read reviews, and book, free on Heallexa.`,
        is_indexable: t.n > 0,
        last_refreshed_at: new Date().toISOString(),
      };
    });

    if (upserts.length === 0) return { materialized: 0 };

    // Chunk to keep payloads reasonable.
    const chunkSize = 100;
    let materialized = 0;
    for (let i = 0; i < upserts.length; i += chunkSize) {
      const chunk = upserts.slice(i, i + chunkSize);
      // INSERT ... ON CONFLICT (slug) DO UPDATE — preserves the original upsert-by-slug.
      const cols = [
        "page_type",
        "slug",
        "specialty",
        "city",
        "state",
        "insurance_id",
        "provider_count",
        "title",
        "description",
        "is_indexable",
        "last_refreshed_at",
      ];
      const valuesSql: string[] = [];
      const params: unknown[] = [];
      for (const u of chunk) {
        const rowPh: string[] = [];
        for (const col of cols) {
          params.push((u as Record<string, unknown>)[col]);
          rowPh.push(`$${params.length}`);
        }
        valuesSql.push(`(${rowPh.join(", ")})`);
      }

      await db.execute(
        `INSERT INTO public.seo_pages (${cols.join(", ")})
         VALUES ${valuesSql.join(", ")}
         ON CONFLICT (slug) DO UPDATE SET
           page_type = EXCLUDED.page_type,
           specialty = EXCLUDED.specialty,
           city = EXCLUDED.city,
           state = EXCLUDED.state,
           insurance_id = EXCLUDED.insurance_id,
           provider_count = EXCLUDED.provider_count,
           title = EXCLUDED.title,
           description = EXCLUDED.description,
           is_indexable = EXCLUDED.is_indexable,
           last_refreshed_at = EXCLUDED.last_refreshed_at`,
        params,
      );
      materialized += chunk.length;
    }
    return { materialized };
  });
